import {
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  KeyboardEventHandler,
  MutableRefObject,
  useState,
} from 'react';
import styled from 'styled-components';

import { ControlConfig, processTextValue } from 'dashboard/components/FormControlPilledInput/types';
import { TextValue, ValueType } from 'dashboard/models/graph-query';
import { paragraphMixins } from 'design-system/mixins/text';

interface EditableItemInputProps {
  controlConfig: ControlConfig;
  deleteItem: () => void;
  deleteLastItem: () => void;
  inputRef: MutableRefObject<HTMLInputElement>;
  isLast: boolean;
  itemKey: string;
  placeholder: string | null;
  value: TextValue;
  onValueChange: (value: TextValue) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  isCompact?: boolean;
}

const EditableInput = styled('input')<{ isCompact: boolean }>`
  ${paragraphMixins.md};
  &::placeholder {
    font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  background: none;
  border: none;

  min-width: 40px;
  width: 100%;
  height: ${({ isCompact }) => (isCompact ? '100%;' : '34px;')};
  display: block;
`;

const EditableItemInput = ({
  deleteItem,
  deleteLastItem,
  inputRef,
  isLast,
  itemKey,
  onValueChange,
  onBlur,
  placeholder,
  value: initialValue,
  // Remove this flag once we've ramped GraphV2 to 100%
  isCompact = false,
}: EditableItemInputProps) => {
  const [value, setValue] = useState<string>(initialValue['raw-value']);
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };
  const commitValue = () => {
    if (value.length === 0) {
      deleteItem();
    } else {
      const processedValue = processTextValue({
        ...initialValue,
        'raw-value': value,
        type: ValueType.Text,
      });

      onValueChange(processedValue);

      // TODO: See if we can remove this by changing 'defaultValue' and
      // making use of useEffect
      setValue('');
    }
  };

  const handleKeyPress: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter' && value.trim() !== '') {
      e.preventDefault();
      commitValue();
    }
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Tab' && value.trim() !== '') {
      e.preventDefault();

      commitValue();
    }

    if (e.key === 'Backspace' && value.trim() === '') {
      if (isLast) {
        deleteLastItem();
      } else {
        deleteItem();
      }
    }
  };

  const handleBlur: FocusEventHandler = (e: FocusEvent<HTMLInputElement>) => {
    if (initialValue['raw-value'] !== value) {
      commitValue();
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <EditableInput
      key={itemKey}
      size={value?.length || placeholder?.length || 3}
      ref={(input) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        inputRef.current = input;
      }}
      value={value}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      placeholder={placeholder}
      isCompact={isCompact}
    />
  );
};

export default EditableItemInput;
